import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { apiClient } from "../../api/httpClient";

export default function ApproveBondsCancellationDialog({
  cancelDialogOpen,
  setCancelDialogOpen,
  isCancelling,
  setIsCancelling,
  selectedBondToCancel,
  cancellationFile,
  setCancellationFile,
  getCancellationInstructionsList,
  handleMarkCancellationComplete,
  handleRevertCancellation,
  isMarkingCancellationComplete,
  refreshData,
}) {
  return (
    <Dialog
      open={cancelDialogOpen}
      onClose={() => !isCancelling && setCancelDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2.5,
          fontWeight: 700,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Bond Cancellation
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => !isCancelling && setCancelDialogOpen(false)}
          edge="end"
          size="small"
          disabled={isCancelling}
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        {selectedBondToCancel && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Cancellation Details
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <strong>Bond Number:</strong> {selectedBondToCancel.bond_number ?? "N/A"}
            </Typography>
            <Typography sx={{ mb: 2 }}>
              <strong>Bond Type:</strong> {selectedBondToCancel.bond_type}
            </Typography>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Documents
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Acceptable Documentation:
            </Typography>
            <ul style={{ paddingLeft: "24px", marginBottom: "20px", listStyleType: "disc" }}>
              {getCancellationInstructionsList(selectedBondToCancel).map(
                (item, idx) => (
                  <li
                    key={`approve-cancel-doc-${idx}-${item}`}
                    style={{ fontSize: "0.9rem", marginBottom: "6px" }}
                  >
                    {item}
                  </li>
                )
              )}
            </ul>

            <Button
              variant="outlined"
              component="label"
              disabled={isCancelling}
              startIcon={<CloudUploadIcon />}
              sx={{ mb: 2 }}
            >
              Choose File{" "}
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  const validTypes = [
                    "application/pdf",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  ];
                  if (file && !validTypes.includes(file.type)) {
                    toast.error("Only PDF or Word documents are allowed.");
                    e.target.value = "";
                    return;
                  }
                  setCancellationFile(file);
                  toast.success("File selected successfully!");
                }}
              />
            </Button>
            {cancellationFile && (
              <Typography variant="caption" sx={{ display: "block", mb: 2 }}>
                Selected File: {cancellationFile.name}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: "100%" }}
          useFlexGap
          flexWrap="wrap"
        >
          {/* Secondary (state) actions on the left */}
          {selectedBondToCancel && (
            <Stack direction="row" spacing={1.25} flexWrap="wrap">
              <Button
                onClick={handleMarkCancellationComplete}
                color="error"
                variant="outlined"
                disabled={isCancelling || isMarkingCancellationComplete}
                startIcon={isMarkingCancellationComplete && <CircularProgress size={16} />}
              >
                {isMarkingCancellationComplete ? "Marking Cancelled..." : "Mark as Cancelled"}
              </Button>

              <Button
                onClick={handleRevertCancellation}
                color="primary"
                variant="outlined"
                disabled={isCancelling || isMarkingCancellationComplete}
                startIcon={isMarkingCancellationComplete && <CircularProgress size={16} />}
              >
                {isMarkingCancellationComplete ? "Reverting..." : "Revert Cancellation"}
              </Button>
            </Stack>
          )}

          {/* Primary actions on the right */}
          <Stack direction="row" spacing={1.25} justifyContent="flex-end">

            <Button
              variant="contained"
              color="primary"
              disabled={!cancellationFile || isCancelling}
              onClick={async () => {
                if (!cancellationFile || !selectedBondToCancel) return;
                setIsCancelling(true);
                const formData = new FormData();
                formData.append("cancellation_file", cancellationFile);
                try {
                  await apiClient(
                    `/cancel-bond/`,
                    "POST",
                    formData,
                    { bond_id: selectedBondToCancel.bond_id },
                    { "Content-Type": "multipart/form-data" }
                  );
                  toast.success("Bond cancelled successfully!");
                  setCancelDialogOpen(false);
                  refreshData();
                } catch (error) {
                  console.error(error);
                  toast.error(error.message || "Failed to cancel the bond.");
                } finally {
                  setIsCancelling(false);
                }
              }}
            >
              {isCancelling ? <CircularProgress size={22} color="inherit" /> : "Submit Cancellation"}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

ApproveBondsCancellationDialog.propTypes = {
  cancelDialogOpen: PropTypes.bool.isRequired,
  setCancelDialogOpen: PropTypes.func.isRequired,
  isCancelling: PropTypes.bool.isRequired,
  setIsCancelling: PropTypes.func.isRequired,
  selectedBondToCancel: PropTypes.object,
  cancellationFile: PropTypes.object,
  setCancellationFile: PropTypes.func.isRequired,
  getCancellationInstructionsList: PropTypes.func.isRequired,
  handleMarkCancellationComplete: PropTypes.func.isRequired,
  handleRevertCancellation: PropTypes.func.isRequired,
  isMarkingCancellationComplete: PropTypes.bool.isRequired,
  refreshData: PropTypes.func.isRequired,
};
